import { useEffect, useRef, useState } from "react";
import { useLang } from "@/lib/i18n";

const TARGET = 0.12;
const KEY = "bqi-music";
const FADE_IN = 2;
const FADE_OUT_AT = 6;
const FADE_OUT_LEN = 8;

export const BackgroundMusic = () => {
  const { lang } = useLang();
  const ref = useRef(null);
  const ctxRef = useRef(null);
  const gainRef = useRef(null);
  const timerRef = useRef(null);
  const [on, setOn] = useState(() => localStorage.getItem(KEY) !== "off");
  const [playing, setPlaying] = useState(false);

  // Volume goes through a Web Audio gain node: iOS Safari ignores audio.volume.
  const graph = () => {
    if (!ctxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const gain = ctx.createGain();
      gain.gain.value = 0;
      ctx.createMediaElementSource(ref.current).connect(gain);
      gain.connect(ctx.destination);
      ctxRef.current = ctx;
      gainRef.current = gain;
    }
    ctxRef.current.resume();
    return [ctxRef.current, gainRef.current.gain];
  };

  const rampTo = (value, seconds) => {
    const [ctx, g] = graph();
    g.cancelScheduledValues(ctx.currentTime);
    g.setValueAtTime(g.value, ctx.currentTime);
    g.linearRampToValueAtTime(value, ctx.currentTime + seconds);
  };

  const stopAfter = (seconds) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      ref.current?.pause();
      setPlaying(false);
    }, seconds * 1000);
  };

  const start = () => {
    const a = ref.current;
    if (!a || !a.paused) return;
    const [ctx, g] = graph();
    a.currentTime = 0;
    a.play().then(() => {
      setPlaying(true);
      const now = ctx.currentTime;
      g.cancelScheduledValues(now);
      g.setValueAtTime(0, now);
      g.linearRampToValueAtTime(TARGET, now + FADE_IN);
      g.setValueAtTime(TARGET, now + FADE_OUT_AT);
      g.linearRampToValueAtTime(0, now + FADE_OUT_AT + FADE_OUT_LEN);
      stopAfter(FADE_OUT_AT + FADE_OUT_LEN + 0.2);
    }).catch(() => {});
  };

  useEffect(() => {
    if (!on) return undefined;
    const events = ["pointerdown", "keydown", "touchend"];
    const handler = () => {
      start();
      events.forEach((e) => window.removeEventListener(e, handler));
    };
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, handler));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on]);

  useEffect(() => () => {
    clearTimeout(timerRef.current);
    ctxRef.current?.close();
  }, []);

  const toggle = (e) => {
    e.stopPropagation();
    if (playing) {
      localStorage.setItem(KEY, "off");
      setOn(false);
      rampTo(0, 0.6);
      stopAfter(0.7);
    } else {
      localStorage.setItem(KEY, "on");
      if (on) start();
      else setOn(true);
    }
  };

  const label = lang === "kz" ? (playing ? "Әуенді өшіру" : "Әуенді қосу") : playing ? "Выключить музыку" : "Включить музыку";

  return (
    <>
      <audio ref={ref} src="/assets/adai-kui.mp3" preload="none" />
      <button
        onPointerDown={(e) => e.stopPropagation()}
        onClick={toggle}
        aria-label={label}
        title={label}
        data-testid="music-toggle-button"
        className="fixed left-4 bottom-4 md:left-6 md:bottom-6 z-50 w-11 h-11 rounded-full border border-white/15 bg-[#0A0D14]/70 backdrop-blur-md grid place-items-center opacity-60 hover:opacity-100 transition-opacity duration-300"
      >
        <span className={`music-bars ${playing ? "is-playing" : ""}`} aria-hidden="true">
          <span /><span /><span /><span />
        </span>
      </button>
    </>
  );
};
